"use client";
import { browserClient } from "@/lib/supabase/clients/browser-client";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { User } from "@supabase/supabase-js";
import { openAuthDialog } from "@/app/_components/account/AuthDialogProvider";

export function AuthButtons() {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [showEntrar, setShowEntrar] = useState(false);

  useEffect(() => {
    console.log('🔵 Setting up auth listener');

    // Check for ?auth=email backdoor. Read after mount (not a render-time lazy
    // init) so SSR and first client render agree — no hydration mismatch.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowEntrar(new URLSearchParams(window.location.search).get("auth") === "email");

    // Get initial session
    browserClient.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    }).catch(() => {
      // Stale refresh token — clear it
      browserClient.auth.signOut();
    });
    
    const {
      data: { subscription },
    } = browserClient.auth.onAuthStateChange((event, session) => {
      // console.group('🔄 Auth State Change');
      // console.log('Event:', event);
      // console.log('Session:', {
      //   user: session?.user?.id,
      //   email: session?.user?.email,
      //   lastSignIn: session?.user?.last_sign_in_at,
      //   authProvider: session?.user?.app_metadata?.provider,
      //   timestamp: new Date().toISOString()
      // });
      // console.groupEnd();

      // Update local state
      setUser(session?.user ?? null);

      // Refresh server components on sign out, token refresh, or user update
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        router.refresh();
      }

      // PostHog analytics. Do NOT identify anonymous users (#486): keep the
      // device's distinct_id anonymous through the free-anon-chat phase so the
      // pre-verification funnel (contact_gate_shown → contact_retrieved) stitches
      // correctly when identify() runs on OTP verification. Also don't reset for
      // anon (that would drop the anonymous device history).
      if (session?.user && !session.user.is_anonymous) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          phone: session.user.phone,
          created_at: session.user.created_at,
        });
      } else if (!session?.user) {
        posthog.reset();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [router]);

  // Anonymous visitors (#486) are not real accounts — don't show the account
  // menu/user icon for them; treat them like logged-out visitors.
  if (!user || user.is_anonymous) {
    if (!showEntrar) return null;
    return (
      <Button
        variant="ghost"
        className="text-sm text-primary hover:text-brand-black/50"
        onClick={() => openAuthDialog("signup")}
      >
        Entrar
      </Button>
    );
  }

  // Verified users get no navbar account chip either — Jaro is anonymous-first
  // and phone-verified, not an account product, so a persistent name/menu in the
  // header adds nothing. The component stays mounted for its auth listener
  // (PostHog identify on verify, reset on sign-out). The ?auth=email backdoor
  // above still exposes an "Entrar" button when needed.
  return null;
}
